import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  NamiCampaignManager,
  NamiPaywallLaunchContext,
} from '@namiml/web-sdk';
import EditableButton from './EditableButton';
import ErrorMessage from './ErrorMessage';
import { NAMI_PAYWALL_CONTEXT } from '../utils/const';
import {
  LaunchContextConfig,
  LaunchContextSelection,
  AttributeConfig,
  buildLaunchContext,
  createDefaultSelection,
  collectTags,
  shouldShow,
} from '../launch-context';

type LaunchContextProps = {
  label?: string | null;
  url?: string | null;
  launchHandler: (
    context: NamiPaywallLaunchContext,
    appSuppliedVideoDelay: number
  ) => void;
};

const LaunchContext = ({ label, url, launchHandler }: LaunchContextProps) => {
  const [config, setConfig] = useState<LaunchContextConfig | null>(null);
  const [selection, setSelection] = useState<LaunchContextSelection | null>(null);
  const [resolvedProductGroups, setResolvedProductGroups] = useState<string[]>([]);
  const [videoDelay, setVideoDelay] = useState('0');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Load config from public JSON
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        const response = await fetch('/launch_context.json');
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const json = (await response.json()) as LaunchContextConfig;
        setConfig(json);
        setSelection(createDefaultSelection(json));
      } catch (err) {
        const details = err instanceof Error ? err.message : String(err);
        setError(`Failed to load launch context config: ${details}`);
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, []);

  // Resolve product groups when config/label/url available
  useEffect(() => {
    if (!config?.productGroups) return;
    const configChoices = config.productGroups.choices ?? [];

    if (config.productGroups.autoPopulateFromPaywall) {
      try {
        const paywallGroups =
          NamiCampaignManager.getProductGroups(
            label ?? undefined,
            url ?? undefined
          ) ?? [];
        const merged = [...new Set([...configChoices, ...paywallGroups])];
        setResolvedProductGroups(merged);
      } catch {
        setResolvedProductGroups(configChoices);
      }
    } else {
      setResolvedProductGroups(configChoices);
    }
  }, [config, label, url]);

  const availableTags = useMemo(() => {
    if (!config) return ['all'];
    return collectTags(config);
  }, [config]);

  const updateSelection = useCallback(
    (partial: Partial<LaunchContextSelection>) => {
      setSelection((prev) => (prev ? { ...prev, ...partial } : prev));
    },
    []
  );

  const handleLaunch = useCallback(() => {
    if (!config || !selection) return;
    try {
      const result = buildLaunchContext(config, selection);
      const context: NamiPaywallLaunchContext = {
        customAttributes: result.customAttributes ?? {},
      };
      if (result.productGroups && result.productGroups.length > 0) {
        context.productGroups = result.productGroups;
      }
      if (result.currentGroup) {
        context.currentGroup = result.currentGroup;
      }
      if (result.customObject) {
        context.customObject = result.customObject;
      }
      localStorage.setItem(NAMI_PAYWALL_CONTEXT, JSON.stringify(context));
      launchHandler(context, Number(videoDelay) || 0);
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to prepare launch context: ${details}`);
    }
  }, [config, selection, launchHandler, videoDelay]);

  if (loading) {
    return (
      <div className="panel stack">
        <h2 className="panel-title">Paywall Launch Context</h2>
        <p className="panel-subtitle">Loading configuration...</p>
      </div>
    );
  }

  if (!config || !selection) {
    return (
      <div className="panel stack">
        <h2 className="panel-title">Paywall Launch Context</h2>
        {error && <ErrorMessage message={error} />}
        <p className="panel-subtitle">No configuration loaded.</p>
      </div>
    );
  }

  return (
    <div className="panel stack">
      <h2 className="panel-title">
        {config.meta?.title ?? 'Paywall Launch Context'}
      </h2>
      <p className="panel-subtitle">
        {config.meta?.description ??
          'Build a context payload, then launch by pressing OK.'}
      </p>
      {error && <ErrorMessage message={error} />}

      {/* Tag Filter */}
      {config.showTags && availableTags.length > 1 && (
        <div className="option-grid">
          {availableTags.map((tag) => (
            <button
              key={tag}
              type="button"
              className={`tv-button${selection.currentTag === tag ? ' active' : ''}`}
              data-testid={`launch-context-tag-${tag}`}
              onClick={() => updateSelection({ currentTag: tag })}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* Product Groups Section */}
      {config.productGroups &&
        shouldShow(config.productGroups.tags, selection.currentTag) && (
          <ProductGroupsSection
            config={config.productGroups}
            resolvedGroups={resolvedProductGroups}
            selection={selection}
            onUpdate={updateSelection}
            label={label}
            url={url}
          />
        )}

      {/* Current Group Section */}
      {config.currentGroup &&
        shouldShow(config.currentGroup.tags, selection.currentTag) &&
        selection.productGroupsEnabled &&
        selection.selectedProductGroupIds.size > 0 && (
          <CurrentGroupSection
            config={config.currentGroup}
            selectedProductGroupIds={selection.selectedProductGroupIds}
            selection={selection}
            onUpdate={updateSelection}
          />
        )}

      {/* Custom Object Section */}
      {config.customObject &&
        shouldShow(config.customObject.tags, selection.currentTag) && (
          <CustomObjectSection
            config={config.customObject}
            selection={selection}
            onUpdate={updateSelection}
          />
        )}

      {/* Custom Attributes Section */}
      {config.customAttributes && (
        <CustomAttributesSection
          config={config.customAttributes}
          selection={selection}
          onUpdate={updateSelection}
        />
      )}

      {/* Video Delay */}
      <EditableButton
        label="App Supplied Video Delay (seconds)"
        value={videoDelay}
        onSubmit={setVideoDelay}
      />

      {/* Launch Button */}
      <button
        type="button"
        className="tv-button"
        id="launch-context-launch-button"
        data-testid="launch-context-launch-button"
        onClick={handleLaunch}
        onKeyDown={(e) =>{
          if (e.keyCode === 142 || e.keyCode === 195) {
            e.preventDefault();
            handleLaunch()
          }
        }}
      >
        Launch Paywall
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Product Groups Section
// ---------------------------------------------------------------------------

interface ProductGroupsSectionProps {
  config: NonNullable<LaunchContextConfig['productGroups']>;
  resolvedGroups: string[];
  selection: LaunchContextSelection;
  onUpdate: (partial: Partial<LaunchContextSelection>) => void;
  label?: string | null;
  url?: string | null;
}

const PRODUCT_PAGE_SIZE = 6;

const ProductGroupsSection = ({
  config,
  resolvedGroups,
  selection,
  onUpdate,
}: ProductGroupsSectionProps) => {
  const [page, setPage] = useState(0);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(resolvedGroups.length / PRODUCT_PAGE_SIZE)),
    [resolvedGroups.length]
  );

  const visibleGroups = useMemo(() => {
    const start = page * PRODUCT_PAGE_SIZE;
    return resolvedGroups.slice(start, start + PRODUCT_PAGE_SIZE);
  }, [resolvedGroups, page]);

  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  const toggleGroup = useCallback(
    (groupId: string) => {
      const next = new Set(selection.selectedProductGroupIds);
      if (config.allowMultiple !== false) {
        if (next.has(groupId)) next.delete(groupId);
        else next.add(groupId);
      } else {
        if (next.has(groupId)) next.clear();
        else {
          next.clear();
          next.add(groupId);
        }
      }
      onUpdate({ selectedProductGroupIds: next });
    },
    [config.allowMultiple, onUpdate, selection.selectedProductGroupIds]
  );

  const sectionTitle = config.sectionTitle ?? 'Product Groups';
  const toggleLabel = config.label || 'Product Groups';

  return (
    <div className="panel" data-testid={config.testId}>
      <div className="panel-title">{sectionTitle}</div>
      {config.description && (
        <div className="panel-subtitle">{config.description}</div>
      )}

      <button
        type="button"
        className={`tv-button${selection.productGroupsEnabled ? ' active' : ''}`}
        data-testid={config.testId ? `${config.testId}-toggle` : undefined}
        onClick={() =>
          onUpdate({ productGroupsEnabled: !selection.productGroupsEnabled })
        }
      >
        {toggleLabel}: {selection.productGroupsEnabled ? 'Enabled' : 'Disabled'}
      </button>

      {selection.productGroupsEnabled && resolvedGroups.length > 0 && (
        <>
          <div className="option-grid" data-layout="column">
            {visibleGroups.map((groupId) => (
              <button
                key={groupId}
                type="button"
                className={`tv-button${selection.selectedProductGroupIds.has(groupId) ? ' active' : ''}`}
                data-testid={`launch-context-product-group-${groupId}`}
                onClick={() => toggleGroup(groupId)}
                onKeyDown={(e) => {
                  if (e.keyCode === 142 || e.keyCode === 195) {
                    e.preventDefault();
                    toggleGroup(groupId)
                  }
                }}
              >
                {groupId}
              </button>
            ))}
          </div>
          {totalPages > 1 && (
            <div
              className="inline-actions"
              style={{ justifyContent: 'space-between' }}
            >
              <button
                type="button"
                className="tv-button"
                onClick={() =>
                  setPage(
                    (prev) => (prev - 1 + totalPages) % totalPages
                  )
                }
              >
                Previous
              </button>
              <span className="helper-text">
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                className="tv-button"
                onClick={() =>
                  setPage((prev) => (prev + 1) % totalPages)
                }
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Current Group Section
// ---------------------------------------------------------------------------

interface CurrentGroupSectionProps {
  config: NonNullable<LaunchContextConfig['currentGroup']>;
  selectedProductGroupIds: Set<string>;
  selection: LaunchContextSelection;
  onUpdate: (partial: Partial<LaunchContextSelection>) => void;
}

const CurrentGroupSection = ({
  config,
  selectedProductGroupIds,
  selection,
  onUpdate,
}: CurrentGroupSectionProps) => {
  const groupIds = useMemo(
    () => Array.from(selectedProductGroupIds),
    [selectedProductGroupIds]
  );

  const sectionTitle = config.sectionTitle ?? 'Current Group';

  return (
    <div className="panel" data-testid={config.testId}>
      <div className="panel-title">{sectionTitle}</div>
      {config.description && (
        <div className="panel-subtitle">{config.description}</div>
      )}

      <button
        type="button"
        className={`tv-button${selection.currentGroupEnabled ? ' active' : ''}`}
        data-testid={config.testId ? `${config.testId}-toggle` : undefined}
        onClick={() =>
          onUpdate({ currentGroupEnabled: !selection.currentGroupEnabled })
        }
        onKeyDown={(e) => {
          if (e.keyCode === 142 || e.keyCode === 195) {
            e.preventDefault();
            onUpdate({ currentGroupEnabled: !selection.currentGroupEnabled })
          }
        }}
      >
        {config.label}: {selection.currentGroupEnabled ? 'Enabled' : 'Disabled'}
      </button>

      {selection.currentGroupEnabled && groupIds.length > 0 && (
        <div className="option-grid" data-layout="column">
          {groupIds.map((id) => (
            <button
              key={id}
              type="button"
              className={`tv-button${selection.selectedCurrentGroupId === id ? ' active' : ''}`}
              data-testid={`launch-context-current-group-${id}`}
              onClick={() =>
                onUpdate({
                  selectedCurrentGroupId:
                    selection.selectedCurrentGroupId === id ? undefined : id,
                })
              }
              onKeyDown={(e) => {
                if (e.keyCode === 142 || e.keyCode === 195) {
                  e.preventDefault();
                  onUpdate({
                    selectedCurrentGroupId: selection.selectedCurrentGroupId === id ? undefined : id,
                  })
                }
              }}
            >
              {id}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Custom Object Section
// ---------------------------------------------------------------------------

interface CustomObjectSectionProps {
  config: NonNullable<LaunchContextConfig['customObject']>;
  selection: LaunchContextSelection;
  onUpdate: (partial: Partial<LaunchContextSelection>) => void;
}

const CustomObjectSection = ({
  config,
  selection,
  onUpdate,
}: CustomObjectSectionProps) => {
  const visibleChoices = useMemo(
    () =>
      config.choices.filter((c) =>
        shouldShow(c.tags, selection.currentTag)
      ),
    [config.choices, selection.currentTag]
  );

  const sectionTitle = config.sectionTitle ?? 'Custom Object';

  return (
    <div className="panel" data-testid={config.testId}>
      <div className="panel-title">{sectionTitle}</div>
      {config.description && (
        <div className="panel-subtitle">{config.description}</div>
      )}

      <button
        type="button"
        className={`tv-button${selection.customObjectEnabled ? ' active' : ''}`}
        data-testid={config.testId ? `${config.testId}-toggle` : undefined}
        onClick={() =>
          onUpdate({ customObjectEnabled: !selection.customObjectEnabled })
        }
        onKeyDown={(e) => {
          if (e.keyCode === 142 || e.keyCode === 195) {
            e.preventDefault();
            onUpdate({ customObjectEnabled: !selection.customObjectEnabled })
          }
        }}
      >
        {config.label}: {selection.customObjectEnabled ? 'Enabled' : 'Disabled'}
      </button>

      {selection.customObjectEnabled && visibleChoices.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <select
            className="tv-button"
            data-testid={config.testId ? `${config.testId}-select` : undefined}
            value={selection.selectedCustomObjectId ?? ''}
            onChange={(e) =>
              onUpdate({
                selectedCustomObjectId: e.target.value || undefined,
              })
            }
            style={{
              width: '100%',
              appearance: 'auto',
              cursor: 'pointer',
            }}
          >
            <option value="">-- Select --</option>
            {visibleChoices.map((choice) => (
              <option
                key={choice.id}
                value={choice.id}
                data-testid={choice.testId}
              >
                {choice.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Custom Attributes Section
// ---------------------------------------------------------------------------

interface CustomAttributesSectionProps {
  config: NonNullable<LaunchContextConfig['customAttributes']>;
  selection: LaunchContextSelection;
  onUpdate: (partial: Partial<LaunchContextSelection>) => void;
}

const CustomAttributesSection = ({
  config,
  selection,
  onUpdate,
}: CustomAttributesSectionProps) => {
  const visiblePresets = useMemo(
    () =>
      (config.presets ?? []).filter((p) =>
        shouldShow(p.tags, selection.currentTag)
      ),
    [config.presets, selection.currentTag]
  );

  const visibleAttributes = useMemo(
    () =>
      (config.attributes ?? []).filter((a) =>
        shouldShow(a.tags, selection.currentTag)
      ),
    [config.attributes, selection.currentTag]
  );

  const handlePresetApply = useCallback(
    (presetId: string) => {
      const preset = config.presets?.find((p) => p.id === presetId);
      if (!preset) return;
      const newValues = { ...selection.attributeValues };
      for (const [key, value] of Object.entries(preset.values)) {
        newValues[key] = value;
      }
      // One tap = full context (mobile TestNami parity): applying a preset also
      // enables the Custom Attributes section so the values reach the launch.
      onUpdate({
        appliedPresetId: presetId,
        attributeValues: newValues,
        customAttributesEnabled: true,
      });
    },
    [config.presets, onUpdate, selection.attributeValues]
  );

  const handleAttributeToggle = useCallback(
    (key: string, enabled: boolean, attr: AttributeConfig) => {
      const newValues = { ...selection.attributeValues };
      if (enabled) {
        newValues[key] = attr.defaultValue ?? getDefaultForType(attr.type);
      } else {
        delete newValues[key];
      }
      onUpdate({ attributeValues: newValues });
    },
    [onUpdate, selection.attributeValues]
  );

  const handleAttributeValueChange = useCallback(
    (key: string, value: unknown) => {
      onUpdate({
        attributeValues: { ...selection.attributeValues, [key]: value },
      });
    },
    [onUpdate, selection.attributeValues]
  );

  const sectionTitle = config.sectionTitle ?? 'Custom Attributes';

  return (
    <div className="panel" data-testid={config.testId}>
      <div className="panel-title">{sectionTitle}</div>
      {config.description && (
        <div className="panel-subtitle">{config.description}</div>
      )}

      <button
        type="button"
        className={`tv-button${selection.customAttributesEnabled ? ' active' : ''}`}
        data-testid={config.testId ? `${config.testId}-toggle` : undefined}
        onClick={() =>
          onUpdate({
            customAttributesEnabled: !selection.customAttributesEnabled,
          })
        }
        onKeyDown={(e) =>{
          if (e.keyCode === 142 || e.keyCode === 195) {
            e.preventDefault();
            onUpdate({
              customAttributesEnabled: !selection.customAttributesEnabled,
            })
          }
        }}
      >
        {config.label}: {selection.customAttributesEnabled ? 'Enabled' : 'Disabled'}
      </button>

      {/* Presets: always visible (not gated behind the section toggle) so a
          single chip tap — e.g. from a Maestro flow — applies the full context.
          Applying a preset enables the section itself (see handlePresetApply). */}
      {visiblePresets.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <div className="helper-text" style={{ marginBottom: '0.5rem' }}>
            Quick Presets
          </div>
          <div className="option-grid">
            {visiblePresets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`tv-button${selection.appliedPresetId === preset.id ? ' active' : ''}`}
                id={preset.testId}
                data-testid={preset.testId}
                onClick={() => handlePresetApply(preset.id)}
                onKeyDown={(e) =>{
                  if (e.keyCode === 142 || e.keyCode === 195) {
                    e.preventDefault();
                    handlePresetApply(preset.id)
                  }
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {selection.customAttributesEnabled && (
        <div className="stack">
          {/* Individual Attributes */}
          {visibleAttributes.map((attr) => (
            <AttributeControl
              key={attr.key}
              attribute={attr}
              currentValue={selection.attributeValues[attr.key]}
              onToggle={(enabled) =>
                handleAttributeToggle(attr.key, enabled, attr)
              }
              onValueChange={(value) =>
                handleAttributeValueChange(attr.key, value)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Attribute Control
// ---------------------------------------------------------------------------

function getDefaultForType(type: string): unknown {
  switch (type) {
    case 'string':
      return '';
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'enum':
      return '';
    default:
      return '';
  }
}

interface AttributeControlProps {
  attribute: AttributeConfig;
  currentValue: unknown;
  onToggle: (enabled: boolean) => void;
  onValueChange: (value: unknown) => void;
}

const AttributeControl = ({
  attribute,
  currentValue,
  onToggle,
  onValueChange,
}: AttributeControlProps) => {
  const isEnabled = currentValue !== undefined;
  const requiredMarker = attribute.required ? ' *' : '';

  const renderControl = () => {
    if (!isEnabled) return null;

    switch (attribute.type) {
      case 'enum':
        return (
          <select
            className="tv-button"
            data-testid={
              attribute.testId ? `${attribute.testId}-select` : undefined
            }
            value={String(currentValue ?? '')}
            onChange={(e) => onValueChange(e.target.value)}
            style={{
              width: '100%',
              appearance: 'auto',
              cursor: 'pointer',
              marginTop: '0.5rem',
            }}
          >
            <option value="">-- Select --</option>
            {attribute.values?.map((val) => (
              <option key={val} value={val}>
                {attribute.valueLabels?.[val] ?? val}
              </option>
            ))}
          </select>
        );

      case 'boolean':
        return (
          <div
            className="inline-actions"
            style={{ marginTop: '0.5rem' }}
          >
            <button
              type="button"
              className={`tv-button${currentValue === true ? ' active' : ''}`}
              data-testid={
                attribute.testId ? `${attribute.testId}-true` : undefined
              }
              onClick={() => onValueChange(true)}
            >
              True
            </button>
            <button
              type="button"
              className={`tv-button${currentValue === false ? ' active' : ''}`}
              data-testid={
                attribute.testId ? `${attribute.testId}-false` : undefined
              }
              onClick={() => onValueChange(false)}
            >
              False
            </button>
          </div>
        );

      case 'string':
        return (
          <input
            type="text"
            className="editable-button-input"
            data-testid={
              attribute.testId ? `${attribute.testId}-input` : undefined
            }
            value={String(currentValue ?? '')}
            placeholder={attribute.placeholder}
            onChange={(e) => onValueChange(e.target.value)}
            style={{ marginTop: '0.5rem', width: '100%', boxSizing: 'border-box' }}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            className="editable-button-input"
            data-testid={
              attribute.testId ? `${attribute.testId}-input` : undefined
            }
            value={currentValue !== undefined && currentValue !== null ? String(currentValue) : ''}
            min={attribute.min}
            max={attribute.max}
            placeholder={attribute.placeholder}
            onChange={(e) => {
              const num = parseFloat(e.target.value);
              onValueChange(isNaN(num) ? '' : num);
            }}
            style={{ marginTop: '0.5rem', width: '100%', boxSizing: 'border-box' }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div data-testid={attribute.testId}>
      <button
        type="button"
        className={`tv-button${isEnabled ? ' active' : ''}`}
        data-testid={
          attribute.testId ? `${attribute.testId}-toggle` : undefined
        }
        onClick={() => onToggle(!isEnabled)}
        style={{ width: '100%', textAlign: 'left' }}
      >
        <span style={{ display: 'block', fontSize: '0.75rem', color: '#9eb2c7' }}>
          {attribute.label}{requiredMarker}
        </span>
        {attribute.description && (
          <span
            className="helper-text"
            style={{ display: 'block', marginTop: '0.2rem' }}
          >
            {attribute.description}
          </span>
        )}
      </button>
      {renderControl()}
    </div>
  );
};

export default LaunchContext;
